import type { ReportingDataInput } from '@risksmart-app/shared/reporting/api/schema';
import type { ReportingDataResponse } from '@risksmart-app/trpc/src/types';
import type { GetReportingDataQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetReportingDataDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createLazyQueryHook } from 'src/utils';

import { mapReportingDataToGraphQL } from './mapReportingData';

/**
 * Imperative `getReportingData` read (RSP-6504) — used by the CSV export
 * pagination loops and the datasource edit-form preview, which fetch on demand
 * with varying paging rather than eagerly on mount.
 */
export const useLazyGetReportingData = createLazyQueryHook<
  ReportingDataInput,
  ReportingDataResponse,
  GetReportingDataQuery
>({
  trpcQuery: (client, args) =>
    client.frontend.reporting.reportingData.query(args),
  mapTrpcDataToGraphQL: mapReportingDataToGraphQL,
  graphqlDocument: GetReportingDataDocument,
  graphqlVariables: (args) => ({ Input: args }),
  // Unlike the eager factory, the lazy factory has no default fetch policy, so
  // this must be set explicitly to keep the pre-migration behaviour (all three
  // replaced call sites passed `no-cache`). `reportingData` is an unkeyed
  // array-of-arrays field on the query root, so caching it would trigger
  // Apollo's "Cache data may be lost when replacing the reportingData field"
  // merge warning — which vitest promotes to a fatal.
  graphqlFetchPolicy: 'no-cache',
  // The datasource edit page drives its preview spinner off `loading`, and
  // re-running Preview with unchanged args would otherwise never flip it.
  notifyOnNetworkStatusChange: true,
  // Both callers already surface the failure themselves — the edit page renders
  // a translated inline `Alert`, and the export loops call `handleError` plus
  // their own `export_failed_message` toast.
  suppressErrorNotification: true,
});
